import { GroupData, ResponseItem } from '@bringg/types';
import _ from 'lodash';
import { MappedStorableItem } from '@bringg/dashboard-sdk/dist/data-entity';
import { binarySearch, comparer, moveItem } from '@bringg-frontend/utils';
import { localLogger } from '@bringg-frontend/bringg-web-infra';

import { GroupUpdater } from './group-updater';
import { fieldValueGetter } from 'bringg-web/features/monitoring/shared/realtime-updates/updates/field-value-getter';
import { tableDefinitionGetter } from 'bringg-web/features/monitoring/shared/realtime-updates/updates/table-definition-getter';

function remove<T extends { id?: number }, K>(
	{ diff }: MappedStorableItem<T>,
	groupUpdater: GroupUpdater<T, K>,
	itemStore: any
) {
	const logPrefix = `realtime remove: item - [${diff.id}]`;
	const currentTableDefinition = tableDefinitionGetter.get();

	const { currentGroupId } = groupUpdater.getGroupIds({ diff });
	const group = currentTableDefinition.groupsMap[currentGroupId];
	const groupItems = groupUpdater.getGroupItems(group);

	if (groupItems) {
		const index = findItemIndexBySort(groupUpdater, { diff }, groupItems, itemStore, true);

		localLogger.log(`${logPrefix} - deleting item from [${currentGroupId}] in index [${index}]`);

		if (index > -1) {
			groupUpdater.removeItem(group, index);
		}
	}
}

function update<T extends { id?: number }, K>(
	{ current, diff }: MappedStorableItem<T>,
	groupUpdater: GroupUpdater<T, K>,
	itemStore: any
) {
	const logPrefix = `realtime update: item - [${current.id}]`;
	const groupIds = groupUpdater.getGroupIds({ current, diff });

	if (!groupIds) {
		return;
	}

	const { newGroupId, currentGroupId } = groupIds;

	const currentTableDefinition = tableDefinitionGetter.get();
	const newGroup = currentTableDefinition.groupsMap[newGroupId];
	const currentGroup = currentTableDefinition.groupsMap[currentGroupId];
	const newGroupItems = groupUpdater.getGroupItems(newGroup);
	const currentGroupItems = groupUpdater.getGroupItems(currentGroup);

	if (newGroupId !== currentGroupId) {
		const index = findItemIndexBySort(groupUpdater, { current, diff }, newGroupItems, itemStore, false);

		if (!isAfterLastItemInPartialGroup(newGroupItems, index, newGroup, newGroupId)) {
			localLogger.log(`${logPrefix} - adding to group [${newGroupId}] in index [${index}]`);
			groupUpdater.addItem(index, current, newGroupId, newGroup);
		}

		if (currentGroup) {
			const currentIndex = findItemIndexBySort(
				groupUpdater,
				{ current, diff },
				currentGroupItems,
				itemStore,
				true
			);

			if (currentIndex > -1) {
				localLogger.log(`${logPrefix} - removing from group [${currentGroupId}] in index [${currentIndex}]`);
				groupUpdater.removeItem(currentGroup, currentIndex);
			}
		}
	} else if (shouldCheckSorting(currentGroupId, diff, groupUpdater)) {
		const currentIndex = diff
			? findItemIndexBySort(groupUpdater, { current, diff }, newGroupItems, itemStore, true)
			: -1;
		const newIndex = findItemIndexBySort(groupUpdater, { current, diff }, newGroupItems, itemStore, false);

		if (currentIndex !== newIndex) {
			if (currentIndex === -1) {
				if (!isAfterLastItemInPartialGroup(newGroupItems, newIndex, newGroup, newGroupId)) {
					localLogger.log(`${logPrefix} - adding to same group [${newGroup}] in index [${newIndex}]`);
					groupUpdater.addItem(newIndex, current, newGroupId, newGroup);
				}
			} else if (isAfterLastItemInPartialGroup(newGroupItems, newIndex, newGroup, newGroupId)) {
				localLogger.log(`${logPrefix} removing from same group [${newGroup}] in index [${currentIndex}]`);
				groupUpdater.removeItem(newGroup, currentIndex);
			} else {
				localLogger.log(
					`${logPrefix} - moving in same group [${newGroup}] from index [${currentIndex}] to index [${newIndex}]`
				);
				moveItem(newGroupItems, currentIndex, currentIndex < newIndex ? newIndex - 1 : newIndex);
			}
		} else if (newGroupId !== undefined) {
			localLogger.log(`${logPrefix} - sorting did not change. staying in ${currentIndex}`);
			touchToTriggerUpdate(newGroupItems, currentIndex);
		}
	} else if (newGroupId !== undefined) {
		const currentIndex = findItemIndexBySort(groupUpdater, { current, diff }, newGroupItems, itemStore, true);

		localLogger.log(
			`${logPrefix} - sorting key did not change and group did not change. staying in ${currentIndex}`
		);
		touchToTriggerUpdate(newGroupItems, currentIndex);
	}
}

function compare<T extends { id?: number }>(item: T | { id: 'null' }, compareValues: any[]) {
	let result = 0;

	if (!item) {
		return 1;
	}

	for (const compareValue of compareValues) {
		result = comparer.compare(fieldValueGetter.get(item, undefined, compareValue.field), compareValue.value);

		if (result !== 0) {
			break;
		}
	}

	return result;
}

function touchToTriggerUpdate<K>(groupItems: K[], currentIndex: number) {
	if (groupItems && groupItems[currentIndex]) {
		groupItems[currentIndex] = { ...groupItems[currentIndex] };
	}
}

function findItemIndexBySort<T extends { id?: number }, K>(
	groupUpdater: GroupUpdater<T, K>,
	{ current, diff }: MappedStorableItem<T>,
	groupItems: K[],
	itemStore: any,
	searchByDiff: boolean
) {
	const sortValues = groupUpdater.getSortValues({ current, diff: searchByDiff ? diff : undefined });
	const currentItems = getCurrentItems({ current, diff }, groupItems, itemStore, sortValues.values);

	const index = binarySearch(currentItems, sortValues.values, sortValues.direction, compare);

	if (!searchByDiff) {
		return index;
	}

	return currentItems[index]?.id === (current || diff).id ? index : -1;
}

function getCurrentItems<T extends { id?: number }, K>(
	{ current, diff }: MappedStorableItem<T>,
	groupItems: K[],
	itemStore: any,
	sortValues: any[]
): T[] {
	const itemId = current ? current.id : diff.id;

	return (groupItems || []).map(groupItem => {
		let id;

		if ((groupItem as GroupData).group_type) {
			const groupId = (groupItem as GroupData).id;

			if (groupId === 'null') {
				return null;
			}

			const parts = groupId?.split('-');
			id = parseInt(parts[parts.length - 1]);
		} else {
			id = (groupItem as ResponseItem).id;
		}

		if (id === itemId && _.has(diff, sortValues[0].field)) {
			return { id: itemId, [sortValues[0].field]: diff[sortValues[0].field] };
		}

		return itemStore.get(id);
	});
}

function shouldCheckSorting<T, K>(newGroupId: string, diff: T, groupUpdater: GroupUpdater<T, K>) {
	if (newGroupId === undefined || !diff) {
		return;
	}

	return groupUpdater.getSortFields(diff).some(field => _.has(diff, field));
}

function isAfterLastItemInPartialGroup<K>(groupItems: K[], index: number, group: GroupData, groupId: string) {
	const currentTableDefinition = tableDefinitionGetter.get();
	const isLastInGroup = groupItems?.length === index;

	if (!isLastInGroup) {
		return false;
	}

	if (!group && groupId === undefined) {
		return true;
	}

	return !currentTableDefinition.isGroupFullyLoaded(group);
}

export const tableDefinitionUpdater = { update, remove };
